import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'

// ── Push-only service worker ─────────────────────────────────
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      await registration.update()
    } catch (err) {
      console.warn('[SW] Push worker registration failed:', err)
    }
  })
}

// ── Push Notification Permission Infrastructure ───────────────
// Exported so the ProfilePage / Settings can request permission
export async function requestPushPermission(): Promise<boolean> {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false

  const result = await Notification.requestPermission()
  return result === 'granted'
}

// Subscribe to push notifications using VAPID public key
// Called after permission is granted; subscription stored in Supabase
export async function subscribePush(): Promise<PushSubscription | null> {
  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY
  if (!vapidKey) {
    console.warn('[Push] VITE_VAPID_PUBLIC_KEY not set — push disabled')
    return null
  }

  const reg = await navigator.serviceWorker.ready
  try {
    const existing = await reg.pushManager.getSubscription()
    if (existing) return existing

    return await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey) as unknown as BufferSource,
    })
  } catch (err) {
    console.warn('[Push] Subscribe failed:', err)
    return null
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
}

// ── React root ────────────────────────────────────────────────
// Recover from stale HTML that points at an asset removed by a newer deploy.
// Vite emits this event when a lazy-loaded chunk cannot be fetched. Reload once
// so the browser receives the current index.html and its matching hashed assets.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  const key = 'wee-vite-preload-reload-at'
  const lastReload = Number(sessionStorage.getItem(key) ?? '0')
  if (Date.now() - lastReload > 15000) {
    sessionStorage.setItem(key, String(Date.now()))
    window.location.reload()
  } else {
    console.error('[App] A lazy-loaded module is still unavailable after refresh.', event)
  }
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)
